import { useAppContext } from '@/context/app';
import { useFilterState } from '@/hooks/FilterModal';
import DEFAULT_OPERATIONS from '@/hooks/FilterModal/filterModal.gql';
import {
  getPathFromState,
  getSearchFromState,
  getStateFromPath,
  getStateFromSearch,
  sortFiltersArray,
  stripHtml,
} from '@/hooks/FilterModal/helpers';
import { useAwaitQuery } from '@/hooks/apollo';
import mergeOperations from '@/util/shallowMerge';
import { STATIC_FILTER_INPUTS } from '@/hooks/FilterModal/filterModal.gql';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GET_PRODUCT_FILTERS } from './getProductFilters.gql';

const DRAWER_NAME = 'filter';

function buildTree(categories) {
  const roots = [];

  categories.forEach(category => {
    const find = categories.find(temp => temp.value === category.parent_id.toString());

    if (!find) {
      roots.push(category);
    } else {
      if (!find.children) {
        find.children = [];
      }

      find.children.push(category);
    }
  });

  return roots;
}

export const useFilterSidebar = props => {
  const { filters = [], aggregations, sortFields, categoryIntId, categoryUrlKey } = props;

  const operations = mergeOperations(DEFAULT_OPERATIONS, props.operations);
  const { getFilterInputsQuery } = operations;

  const [isApplying, setIsApplying] = useState(false);

  const productFiltersQuery = useAwaitQuery(GET_PRODUCT_FILTERS);

  const [{ drawer }, { toggleDrawer, closeDrawer }] = useAppContext();

  const [filterState, filterApi] = useFilterState();

  const isOpen = drawer === DRAWER_NAME;

  const [categoryTree, setCategoryTree] = useState([]);

  const [categoryOptions, setCategoryOptions] = useState([]);

  const [featuredCategories, setFeaturedCategories] = useState([]);

  const router = useRouter();

  const pathname = usePathname();

  const search = useSearchParams();

  const introspectionData = STATIC_FILTER_INPUTS;

  const isSearchPage = useMemo(() => {
    return pathname === '/search.html';
  }, [pathname]);

  const attributeCodes = useMemo(() => filters?.map(({ attribute_code }) => attribute_code), [filters]);

  // Create a set of disabled filters.
  const DISABLED_FILTERS = useMemo(() => {
    const disabled = new Set();

    // Disable category filtering when not on a search page.
    if (pathname !== '/search.html') {
      disabled.add('category_id');
      disabled.add('category_uid');
    }

    return disabled;
  }, [pathname]);

  const isBooleanFilter = options => {
    const optionsString = JSON.stringify(options);

    return (
      options.length <= 2 &&
      (optionsString.includes(
        JSON.stringify({
          __typename: 'AggregationOption',
          label: '0',
          value: '0',
        }),
      ) ||
        optionsString.includes(
          JSON.stringify({
            __typename: 'AggregationOption',
            label: '1',
            value: '1',
          }),
        ))
    );
  };

  // iterate over filters once to set up all the collections we need
  const [filterNames, filterKeys, filterItems, filterFrontendInput] = useMemo(() => {
    const names = new Map();
    const keys = new Set();
    const frontendInput = new Map();
    const itemsByGroup = new Map();
    // console.log('filters', filters);
    const sortedFilters = sortFiltersArray([...(filters || [])]);

    for (const filter of sortedFilters) {
      const { options, label: name, attribute_code: group } = filter;

      const isValid = attributeCodes.includes(group);
      const isEnabled = !DISABLED_FILTERS.has(group);

      // If this aggregation is not a possible filter, just back out.
      if (isValid && isEnabled) {
        const items = [];

        // add filter name
        names.set(group, name);

        // add filter key permutations
        keys.add(`${group}`);

        // TODO: Get all frontend input type from gql if other filter input types are needed
        // See https://github.com/magento-commerce/magento2-pwa/pull/26
        if (isBooleanFilter(options)) {
          frontendInput.set(group, 'boolean');
          // add items
          items.push({
            title: 'No',
            value: '0',
            label: name + ':' + 'No',
          });
          items.push({
            title: 'Yes',
            value: '1',
            label: name + ':' + 'Yes',
          });
        } else {
          // Add frontend input type
          frontendInput.set(group, null);

          // add items
          for (const { label, value, swatch_data, count } of options) {
            items.push({
              title: stripHtml(label),
              value,
              swatch_data,
              count,
            });
          }
        }

        itemsByGroup.set(group, items);
      }
    }

    return [names, keys, itemsByGroup, frontendInput];
  }, [DISABLED_FILTERS, attributeCodes, filters]);

  // on apply, write filter state to location
  useEffect(() => {
    if (isApplying) {
      if (isSearchPage) {
        const nextSearch = getSearchFromState(search, filterKeys, filterState);

        router.push(`${pathname}${nextSearch}`);
      } else {
        const nextPath = getPathFromState({
          categoryUrlKey,
          aggregations,
          initialPath: pathname,
          filterState,
        });
        console.log('nextPath', nextPath);
        router.push(nextPath);
      }

      // mark the operation as complete
      setIsApplying(false);
    }
  }, [
    filterKeys,
    filterState,
    router,
    isApplying,
    pathname,
    search,
    aggregations,
    isSearchPage,
    filters,
    categoryUrlKey,
  ]);

  const handleOpen = useCallback(() => {
    toggleDrawer(DRAWER_NAME);
  }, [toggleDrawer]);

  const handleClose = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const handleApply = useCallback(() => {
    console.log('handleApply');
    setIsApplying(true);
    handleClose();
  }, [handleClose]);

  const handleReset = useCallback(() => {
    filterApi.clear();
    setIsApplying(true);
  }, [filterApi, setIsApplying]);

  const handleKeyDownActions = useCallback(
    event => {
      // do not handle keyboard actions when the modal is closed
      if (!isOpen) {
        return;
      }

      switch (event.keyCode) {
        // when "Esc" key fired -> close the modal
        case 27:
          handleClose();
          break;
      }
    },
    [isOpen, handleClose],
  );
  const handleGenerateLinkHref = ({ group, item }) => {
    const nextState = new Map(filterState);
    const nextSet = new Set(filterState.get(group));

    nextSet.add(item);
    nextState.set(group, nextSet);
    console.log('newFilterState', filterState, nextState);
    const nextPath = getPathFromState({
      categoryUrlKey,
      aggregations,
      initialPath: pathname,
      filterState: nextState,
    });
    return nextPath;
  };
  useEffect(() => {
    const getCategoryTree = async () => {
      let categoryTree = [];

      try {
        const { data: productFilterData } = await productFiltersQuery({
          nextFetchPolicy: 'cache-first',
          variables: {
            filters: {
              category_id: {
                eq: categoryIntId,
              },
            },
          },
        });

        const aggregations = productFilterData?.productFilters?.aggregations?.concat();

        if (aggregations) {
          const categories = aggregations.find(temp => temp.attribute_code === 'category_id');

          const categoryOptions = categories.options;

          setCategoryOptions(categoryOptions);

          const copy = [];

          categoryOptions.forEach(temp => {
            copy.push(Object.assign({}, temp));
          });

          categoryTree = buildTree(copy);
          setCategoryTree(categoryTree);
        }
      } catch (error) {
        console.error(error);

        return null;
      }
    };

    if (categoryIntId) {
      getCategoryTree();
    }
  }, [categoryIntId, productFiltersQuery]);

  useEffect(() => {
    if (isSearchPage) {
      const nextState = getStateFromSearch(search, filterKeys, filterItems);

      filterApi.setItems(nextState);
    } else {
      const nextState = getStateFromPath({
        categoryUrlKey,
        aggregations,
        sortFields,
        pathname,
        filters,
      });

      filterApi.setItems(nextState);
    }
  }, [
    aggregations,
    sortFields,
    pathname,
    filterApi,
    filterItems,
    filterKeys,
    filters,
    search,
    isSearchPage,
    categoryUrlKey,
  ]);

  return {
    filterApi,
    filterItems,
    filterKeys,
    filterNames,
    filterFrontendInput,
    filterState,
    handleApply,
    handleClose,
    handleKeyDownActions,
    handleOpen,
    handleReset,
    isApplying,
    isOpen,
    categoryTree,
    featuredCategories,
    handleGenerateLinkHref,
  };
};
